/**
 * CreateAgentAdvanced — "More settings" behind the simple create-agent card
 * (CreateAgentFlow). One screen, all steps visible, as in
 * kiaan-create-agent-v2.html §1 and create-agent-dev-tree-v2.html §1.
 *
 * Steps: 1 Job · 2 Team & boss · 3 Brain (AI model) · 4 Knowledge ·
 * 5 Code & git (Developer agents only) · Tools & folders · Rules & test.
 * When the agent is not a Developer, step 5 is not rendered at all and the
 * later steps renumber (6 steps in total).
 */
import { useEffect, useMemo, useState } from 'react';
import { createAgent, testAgent, KIAAN_UNREACHABLE, type KiaanAgent, type KiaanAgentInput } from './kiaanApi';

export type AgentKind = 'Assistant' | 'Developer';

export interface CreateAgentAdvancedProps {
  agents: KiaanAgent[];
  onClose: () => void;
  onCreated: (agent: KiaanAgent) => void;
  /** Start from the card Kiaan filled. */
  initial?: KiaanAgentInput & { kind?: AgentKind };
  /** Open with this step highlighted (the card line the user tapped). */
  initialStep?: string;
  /** Back to the simple card. */
  onBack?: () => void;
}

const DEFAULT_PACKS: { key: string; label: string }[] = [
  { key: 'invoices_payments', label: 'Invoices & payments' },
  { key: 'customer_list', label: 'Customer list' },
  { key: 'product_prices', label: 'Product prices' },
  { key: 'cost_prices', label: 'Cost prices' },
  { key: 'salaries_hr', label: 'Salaries / HR' },
  { key: 'company_rules', label: 'Company rules' },
];

const TOOL_OPTIONS: { name: string; icon: string; levels: string[] }[] = [
  { name: 'Folders', icon: '📁', levels: ['off', 'read', 'read + write'] },
  { name: 'ChatKart WhatsApp', icon: '💬', levels: ['off', 'draft only', 'send'] },
  { name: 'MailKart email', icon: '📧', levels: ['off', 'draft only', 'send'] },
  { name: 'Agent computer', icon: '🖥', levels: ['off', 'on'] },
];

const CODE_TREE_CHOICES: { path: string; locked?: boolean }[] = [
  { path: 'backend/modules/leads/' },
  { path: 'backend/modules/quotes/' },
  { path: 'backend/modules/' },
  { path: 'frontend/pages/leads/' },
  { path: 'frontend/pages/' },
  { path: 'backend/core/auth/', locked: true },
  { path: 'mobile/', locked: true },
];

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'agent';
}

export function CreateAgentAdvanced({ agents, onClose, onCreated, initial, initialStep, onBack }: CreateAgentAdvancedProps) {
  const [kind, setKind] = useState<AgentKind>(initial?.kind ?? 'Assistant');
  const [name, setName] = useState(initial?.name ?? '');
  const [job, setJob] = useState(initial?.job ?? '');
  const teams = useMemo(() => Array.from(new Set(agents.map((a) => a.teamName).filter(Boolean))), [agents]);
  const startTeam = initial?.teamName ?? '';
  const [team, setTeam] = useState(startTeam && teams.includes(startTeam) ? startTeam : (teams[0] ?? ''));
  const [newTeam, setNewTeam] = useState(startTeam && !teams.includes(startTeam) ? startTeam : '');
  const [boss, setBoss] = useState<string>(initial?.bossAgentId ?? '');
  const [brain, setBrain] = useState<KiaanAgentInput['brain']>(initial?.brain ?? 'claude');
  const [limit, setLimit] = useState<string>(initial?.monthlyLimit != null ? String(initial.monthlyLimit) : '2000');
  const [packs, setPacks] = useState<Set<string>>(new Set(initial?.knowledgePacks ?? ['invoices_payments', 'customer_list', 'product_prices', 'company_rules']));
  const [codeTree, setCodeTree] = useState<Set<string>>(new Set(initial?.codeTree ?? []));
  const [customPath, setCustomPath] = useState('');
  const [tools, setTools] = useState<Record<string, string>>(
    initial?.tools?.length
      ? Object.fromEntries(initial.tools.map((t) => [t.name, t.access]))
      : { Folders: 'read', 'ChatKart WhatsApp': 'draft only', 'MailKart email': 'draft only', 'Agent computer': 'off' },
  );
  const [schedule, setSchedule] = useState(initial?.rules?.schedule ?? 'every day 9:00');
  const [sendsMessage, setSendsMessage] = useState(initial?.rules?.sendsMessage ?? 'ask first');
  const [changesMoney, setChangesMoney] = useState(initial?.rules?.changesMoney ?? 'never');
  const [active, setActive] = useState(1);
  const [busy, setBusy] = useState<'test' | 'save' | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'bad'; text: string } | null>(null);

  const isDev = kind === 'Developer';
  const steps = useMemo(() => {
    const s = ['Job', 'Team & boss', 'Brain (AI model)', 'Knowledge'];
    if (isDev) s.push('Code & git');
    s.push('Tools & folders', 'Rules & test');
    return s;
  }, [isDev]);
  const stepNo = (label: string) => steps.indexOf(label) + 1;
  useEffect(() => {
    if (initialStep && steps.includes(initialStep)) setActive(steps.indexOf(initialStep) + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const teamName = newTeam.trim() || team;

  const input = (): KiaanAgentInput => ({
    name: name.trim(),
    job: job.trim(),
    teamName,
    bossAgentId: boss || null,
    brain,
    monthlyLimit: limit ? Number(limit) : null,
    knowledgePacks: Array.from(packs),
    ...(isDev ? { codeTree: Array.from(codeTree) } : {}),
    tools: TOOL_OPTIONS.map((t) => ({ name: t.name, access: tools[t.name] ?? 'off' })),
    rules: { schedule, sendsMessage, changesMoney },
  });

  const canSave = name.trim().length > 0 && job.trim().length > 0 && teamName.length > 0;

  const doTest = async () => {
    if (busy) return;
    setBusy('test');
    setNotice(null);
    try {
      const r = await testAgent(input());
      setNotice({ kind: r.ok ? 'ok' : 'bad', text: r.message || (r.ok ? 'Test passed.' : 'Test did not pass.') });
    } catch {
      setNotice({ kind: 'bad', text: KIAAN_UNREACHABLE });
    } finally {
      setBusy(null);
    }
  };

  const doSave = async () => {
    if (busy || !canSave) return;
    setBusy('save');
    setNotice(null);
    try {
      const a = await createAgent(input());
      onCreated(a);
    } catch {
      setNotice({ kind: 'bad', text: KIAAN_UNREACHABLE });
    } finally {
      setBusy(null);
    }
  };

  const togglePack = (k: string) => setPacks((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleTree = (p: string) => setCodeTree((s) => { const n = new Set(s); if (n.has(p)) n.delete(p); else n.add(p); return n; });
  const cycleTool = (t: { name: string; levels: string[] }) => setTools((cur) => {
    const i = t.levels.indexOf(cur[t.name] ?? 'off');
    return { ...cur, [t.name]: t.levels[(i + 1) % t.levels.length] };
  });

  const box = (label: string, children: React.ReactNode) => {
    const n = stepNo(label);
    return (
      <div className="kw-box" style={active === n ? { outline: '2px solid var(--saffron)', outlineOffset: 1 } : undefined} onClick={() => setActive(n)} data-testid={`agent-step-${slug(label)}`}>
        <h4>{n} · {label}</h4>
        {children}
      </div>
    );
  };

  return (
    <div className="kw-body" data-testid="create-agent-flow">
      <div className="kw-head" style={{ background: 'transparent' }}>
        {onBack && <button type="button" className="kw-btn kw-light" style={{ marginTop: 0 }} onClick={onBack}>← Back to the card</button>}
        <span className="kw-small">· Agents · New agent · More settings</span>
        <span className="kw-sp" />
        <button type="button" className="kw-icon-btn" onClick={onClose} aria-label="Close new agent">✕</button>
      </div>
      <div className="kw-cols kw-two" style={{ gridTemplateColumns: '190px 1fr' }}>
        <aside className="kw-steps" aria-label="Steps">
          {steps.map((label, i) => {
            const n = i + 1;
            const done = n < active;
            return (
              <button key={label} type="button" aria-label={`${n} · ${label}`} className={`kw-sx${done ? ' kw-d' : ''}${active === n ? ' kw-on' : ''}`} onClick={() => setActive(n)}>
                <span className="kw-c">{done ? '✓' : n}</span>{n} · {label}
              </button>
            );
          })}
          <div className="kw-small" style={{ marginTop: 14 }}>
            {isDev ? 'Step 5 shows only for Developer agents.' : <>You can also just say it:<br />&quot;Make me an agent that chases late payments&quot;</>}
          </div>
        </aside>

        <div className="kw-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          {box('Job', (
            <>
              <input className="kw-fld" placeholder="Name, e.g. Payment reminder" value={name} onChange={(e) => setName(e.target.value)} aria-label="Agent name" />
              <textarea className="kw-fld" rows={3} placeholder="What it does, in one or two lines" value={job} onChange={(e) => setJob(e.target.value)} aria-label="Agent job" />
              <div style={{ display: 'flex', gap: 6 }}>
                {(['Assistant', 'Developer'] as AgentKind[]).map((k) => (
                  <button key={k} type="button" className={`kw-opt${kind === k ? ' kw-on' : ''}`} onClick={() => setKind(k)} aria-pressed={kind === k}>
                    <span className="kw-rd" />{k}
                  </button>
                ))}
              </div>
              <div className="kw-small">Kiaan writes the job text from your one line. Edit if needed.</div>
            </>
          ))}

          {box('Team & boss', (
            <>
              {teams.map((t) => (
                <button key={t} type="button" className={`kw-opt${team === t && !newTeam ? ' kw-on' : ''}`} onClick={() => { setTeam(t); setNewTeam(''); }}>
                  <span className="kw-rd" />{t}
                </button>
              ))}
              <input className="kw-fld" placeholder={teams.length ? 'Or a new team' : 'Team name, e.g. Accounts team'} value={newTeam} onChange={(e) => setNewTeam(e.target.value)} aria-label="Team" />
              <div className="kw-row">Reports to
                <select className="kw-fld" style={{ marginLeft: 'auto', width: 'auto' }} value={boss} onChange={(e) => setBoss(e.target.value)} aria-label="Reports to">
                  <option value="">Kiaan (main)</option>
                  {agents.filter((a) => !a.main).map((a) => <option key={a.id} value={a.id}>{a.name} (AI)</option>)}
                </select>
              </div>
              <div className="kw-row">Human owner <span className="kw-pill kw-ok kw-ml">you</span></div>
            </>
          ))}

          {box('Brain (AI model)', (
            <>
              {([
                ['claude', 'Claude (smart)', 'Claude SDK'],
                ['openrouter', 'Fast & cheap', 'OpenRouter'],
                ['auto', 'Let Kiaan choose per task', ''],
              ] as [KiaanAgentInput['brain'], string, string][]).map(([v, label, tag]) => (
                <button key={v} type="button" className={`kw-opt${brain === v ? ' kw-on' : ''}`} onClick={() => setBrain(v)}>
                  <span className="kw-rd" />{label}{tag && <span className="kw-pill kw-wip kw-ml">{tag}</span>}
                </button>
              ))}
              <div className="kw-small">Monthly limit ₹ <input className="kw-fld" style={{ width: 90, display: 'inline-block', margin: '0 4px' }} value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^\d]/g, ''))} aria-label="Monthly limit" /> · used ₹0</div>
            </>
          ))}

          {box('Knowledge', (
            <>
              {DEFAULT_PACKS.map((p) => (
                <button key={p.key} type="button" className="kw-row" style={{ width: '100%', background: 'transparent', border: 0, borderBottom: '1px dashed var(--line)', cursor: 'pointer', textAlign: 'left', color: 'inherit' }} onClick={() => togglePack(p.key)} aria-pressed={packs.has(p.key)}>
                  {p.label}<span className={`kw-pill kw-ml ${packs.has(p.key) ? 'kw-ok' : 'kw-no'}`}>{packs.has(p.key) ? 'yes' : 'no'}</span>
                </button>
              ))}
              <div className="kw-small">Starts from the team&apos;s role. Change only what you need.</div>
            </>
          ))}

          {isDev && box('Code & git', (
            <>
              <div className="kw-row">Repo <span className="kw-mono kw-ml">aura-sunstellar</span></div>
              <div className="kw-row">Kept on <span className="kw-pill kw-ok kw-ml">Sun Stellar dev server · Gitea</span></div>
              <div className="kw-small" style={{ marginTop: 6 }}>Which part it may change</div>
              <div className="kw-tree">
                {CODE_TREE_CHOICES.map((c) => c.locked ? (
                  <div key={c.path}><span className="kw-lock">{c.path} 🔒</span></div>
                ) : (
                  <label key={c.path} style={{ display: 'block', cursor: 'pointer' }}>
                    <input type="checkbox" checked={codeTree.has(c.path)} onChange={() => toggleTree(c.path)} style={{ marginRight: 6 }} />
                    <span className={codeTree.has(c.path) ? 'kw-sel' : ''}>{c.path}</span>
                  </label>
                ))}
                {Array.from(codeTree).filter((p) => !CODE_TREE_CHOICES.some((c) => c.path === p)).map((p) => (
                  <label key={p} style={{ display: 'block' }}>
                    <input type="checkbox" checked onChange={() => toggleTree(p)} style={{ marginRight: 6 }} />
                    <span className="kw-sel">{p}</span>
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <input className="kw-fld" placeholder="Add a folder path" value={customPath} onChange={(e) => setCustomPath(e.target.value)} aria-label="Add a folder path" />
                <button type="button" className="kw-btn kw-light" style={{ marginTop: 4 }} onClick={() => { const p = customPath.trim(); if (p) { toggleTree(p.endsWith('/') ? p : `${p}/`); setCustomPath(''); } }}>Add</button>
              </div>
              <div className="kw-row">Own worktree <span className="kw-mono kw-ml">/home/agent/work/{slug(name || 'agent')}</span></div>
              <div className="kw-row">Branches <span className="kw-mono kw-ml">kiaan/*</span></div>
              <div className="kw-row">Merge into develop <span className="kw-pill kw-wt kw-ml">asks you first</span></div>
              <div className="kw-row">Go live <span className="kw-pill kw-bad kw-ml">Ardhanex approves</span></div>
              <div className="kw-row">Code search <span className="kw-pill kw-ok kw-ml">code map (graph) only</span></div>
              <div className="kw-row">Live app <span className="kw-pill kw-no kw-ml">never</span></div>
            </>
          ))}

          {box('Tools & folders', (
            <>
              {TOOL_OPTIONS.map((t) => (
                <button key={t.name} type="button" className="kw-row" style={{ width: '100%', background: 'transparent', border: 0, borderBottom: '1px dashed var(--line)', cursor: 'pointer', textAlign: 'left', color: 'inherit' }} onClick={() => cycleTool(t)}>
                  {t.icon} {t.name}
                  <span className={`kw-pill kw-ml ${tools[t.name] === 'off' ? 'kw-no' : tools[t.name]?.startsWith('read') || tools[t.name] === 'on' ? 'kw-wip' : 'kw-wt'}`}>{tools[t.name] ?? 'off'}</span>
                </button>
              ))}
              <div className="kw-small">Click a line to change it.</div>
            </>
          ))}

          {box('Rules & test', (
            <>
              <div className="kw-row">Runs <input className="kw-fld" style={{ width: 130, marginLeft: 'auto' }} value={schedule} onChange={(e) => setSchedule(e.target.value)} aria-label="Runs" /></div>
              <div className="kw-row">Sends a message
                <select className="kw-fld" style={{ width: 'auto', marginLeft: 'auto' }} value={sendsMessage} onChange={(e) => setSendsMessage(e.target.value)} aria-label="Sends a message">
                  <option value="ask first">ask first</option>
                  <option value="draft only">draft only</option>
                  <option value="allowed">allowed</option>
                </select>
              </div>
              <div className="kw-row">Changes money data
                <select className="kw-fld" style={{ width: 'auto', marginLeft: 'auto' }} value={changesMoney} onChange={(e) => setChangesMoney(e.target.value)} aria-label="Changes money data">
                  <option value="never">never</option>
                  <option value="ask first">ask first</option>
                </select>
              </div>
              {notice && <div className={`kw-banner ${notice.kind === 'ok' ? 'kw-ok' : 'kw-danger'}`} role="status" style={{ margin: '6px 0 0' }}>{notice.text}</div>}
              <button type="button" className="kw-btn" onClick={() => void doTest()} disabled={busy !== null}>{busy === 'test' ? 'Testing…' : 'Test with 1 item'}</button>
              <button type="button" className="kw-btn kw-light" onClick={() => void doSave()} disabled={busy !== null || !canSave}>{busy === 'save' ? 'Saving…' : 'Save agent'}</button>
            </>
          ))}
        </div>
      </div>
    </div>
  );
}
